import React, {useState} from 'react';
import {useExperiments, useTranslation} from '@wix/yoshi-flow-editor';
import {useControllerProps} from '../../../../../../../domain/controllers/ControllerContext';
import {SPECS} from '../../../../../../../domain/specs';
import {InlineAddressForm} from './InlineAddressForm/InlineAddressForm';
import s from './DestinationPicker.scss';

type DestinationPickerProps = {
  dataHook: string;
};

export enum DestinationPickerDataHook {
  Button = 'DestinationPicker.Button',
}

// eslint-disable-next-line sonarjs/cognitive-complexity
export const DestinationPicker = ({dataHook}: DestinationPickerProps) => {
  const {t} = useTranslation();
  const {experiments} = useExperiments();
  const [isInlineFormOpen, setIsInlineFormOpen] = useState(false);
  const {handleChangeRegion, shouldDisplayDelivery, shouldDisplayTax, shouldHideCountryPicker} =
    useControllerProps().orderStore;
  const {cartModel, isNonShippableCart} = useControllerProps().cartStore;

  const getChooseRegionTranslationKey = (): string => {
    if (shouldDisplayDelivery && shouldDisplayTax) {
      return 'cart.shipping_destination.shipping_and_tax.choose_region';
    }
    if (shouldDisplayDelivery) {
      return 'cart.shipping_destination.choose_region';
    }
    return 'cart.shipping_destination.tax.choose_region';
  };

  const getRegion = (): string => {
    const {deliveryInfo, paymentInfo} = cartModel;
    const address = isNonShippableCart ? paymentInfo?.address : deliveryInfo?.address;

    if (address?.countryFullname && address?.subdivisionFullname) {
      return t('cart.estimated_shipping_address', {
        country: address.countryFullname,
        subdivision: address.subdivisionFullname,
      });
    }
    if (address?.countryFullname) {
      return address.countryFullname;
    }
  };

  const getAriaLabel = () => {
    const region = getRegion();
    return region ? t('cart.sr_change_destination', {destinationName: region}) : t(getChooseRegionTranslationKey());
  };

  function getButtonText() {
    const region = getRegion();
    return region ?? t(getChooseRegionTranslationKey());
  }

  const handleClick = () => {
    if (experiments.enabled(SPECS.ecomInlineAddressSelectionInCart)) {
      setIsInlineFormOpen((prev) => !prev);
    } else {
      void handleChangeRegion();
    }
  };

  return shouldHideCountryPicker ? null : (
    <div className={s.location} data-hook={dataHook}>
      <button
        className={s.countryPickerButton}
        data-hook={DestinationPickerDataHook.Button}
        type="button"
        onClick={handleClick}
        aria-haspopup={true}
        aria-describedby="change-region-description"
        aria-label={getAriaLabel()}>
        {getButtonText()}
      </button>
      {isInlineFormOpen && <InlineAddressForm onClose={() => setIsInlineFormOpen(false)} />}
    </div>
  );
};
